import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccidentsService } from '../../../../../services/accidents.service';

import {
  ClaimResponse,
  Coverage,
  CoveragesRequest,
  PoliciesRequest,
  Vehicle,
  VehiclesRequest,
} from '../../../../../interfaces/accidents.interface';
import { descargarConsultaAccidente } from '../../../../../helpers/descargarAvisoAccidentes';
import { DocumentosRequeridosComponent } from '../documentos-requeridos/documentos-requeridos.component';
import { NotificationComponent } from '../../../../pagos/pagos-online/helpers/notification.component';
import { AuthService } from '../../../../../services/auth.service';
import { GoogleAnalyticsService } from '../../../../../services/google-analytics/google-analytics.service';

@Component({
  selector: 'accidentes-consulta',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DocumentosRequeridosComponent,
    NotificationComponent,
  ],
  templateUrl: './consulta.component.html',
  styleUrl: './consulta.component.css',
})
export class ConsultaComponent {
  constructor(
    private accidentService: AccidentsService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService
  ) {}

  // Propiedades de consulta
  public eventNumber: string = '';
  public showModal: boolean = false;
  public channelCode: string = 'iE6vK8mY3qF9sX'; // Channels.Intermediario
  public channel: number = 5; // Channel Intermediario

  // Data de consulta
  public policy?: string;
  public isLoading: boolean = false;
  public claimConsultData?: ClaimResponse;
  public document?: string;
  public vehicle?: Vehicle;
  public coverage?: Coverage;
  public lossDateFormatted?: string;
  public lossDate?: Date;
  public emailAddress?: string;
  public phoneNumber?: string;

  public documents = [
    { description: 'RNC', code: 1, length: 9 },
    { description: 'Cédula', code: 2, length: 11 },
    { description: 'Pasaporte', code: 3, length: 9 },
  ];

  // Notificaciones
  public showNotification = false;
  public notificationType: 'success' | 'error' | 'warning' = 'success';
  public notificationTitle = '';
  public notificationMessage = '';

  // Consulta de reclamo
  onSubmitConsult(from: string = '') {
    if (this.eventNumber && isNaN(parseInt(this.eventNumber.trim()))){
      this.eventNumber = '';
      return;
    };
    const request = { EventNumber: this.eventNumber.replace(/[^0-9-]/g, '').trim() };

    this.isLoading = true;
    this.accidentService.getData('Claims', request).subscribe({
      next: (response: ClaimResponse) => {
        this.claimConsultData = response;
        this.policy = response.policyNumber;
        this.document = this.documents.find(
          (d) => d.code === response.clientInfo.idType
        )?.description;
        this.lossDate= response.lossDate;
        this.lossDateFormatted = new Date(response.lossDate).toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });


        this.gAnalytics.trackEvent('consulta_reclamo',{
        page:'/accidentes',
        action:'Consulta de un reclamo'
        })


        const policy = response.policyNumber;
        const unitNumber = response.unitNumber;
        const covergaCode = response.coverageCode;
        const lossdate = response.lossDate;

        this.getVehicles(lossdate, policy, unitNumber);
        this.getCoverages(lossdate, policy, unitNumber, covergaCode);
        this.getPolicies();

        // Despliegue de información de consulta
        if (from !== 'completing files') {
          this.onShowModal();
        }
      },
      error: (error) => {
        if (error.status === 404) {
          setTimeout(() => {
            this.onShowNotificacion(
              'error',
              '',
              'No se ha encontrado reclamo con este número.'
            );
          }, 500);
        }
        console.error('Error al cargar obtener los datos:', error);
      },
    });
  }

  // Obtener datos de vehículos
  getVehicles(LossDate: Date, PolicyNumber: string, unitNumber: number) {
    const request: VehiclesRequest = {
      LossDate,
      PolicyNumber,
      Channel: this.channel,
      ChannelCode: this.channelCode
    };

    this.accidentService.getData('Vehicles', request).subscribe({
      next: (response) => {
        if (response.messages.length) {
          response.messages.map((message, idx) => {
            setTimeout(() => {
              this.onShowNotificacion('warning', '', message.description);
            }, 6000 * idx);
          });
        }
        if (response.vehicles.length) {
          this.vehicle = response.vehicles.find(
            (v) => v.vehicleNumber === unitNumber
          );
        }
      },
      error: (error) => {
        console.error('Error al obtener los datos de vehículos:', error);
        if (error.statusCode === 404) {
          console.warn('No se encontraron vehículos asociados a la póliza.');
        }
      },
    });
  }

  // Obtener datos de coberturas
  getCoverages(
    LossDate: Date,
    PolicyNumber: string,
    UnitNumber: number,
    coverageCode: number
  ) {
    const request: CoveragesRequest = {
      LossDate,
      PolicyNumber,
      ChannelCode: this.channelCode,
      Channel: this.channel,
      UnitNumber,
      GetGeneralCoverages: true,
    };

    this.accidentService.getData('Coverages', request).subscribe({
      next: (response) => {
        if (response.coverages.length) {
          this.coverage = response.coverages.find(
            (c) => c.coverageCode === coverageCode
          );
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error al obtener las coberturas:', error);
        if (error.statusCode === 404) {
          console.warn(
            'No se encontraron coberturas para los datos ingresados.'
          );
        }
      },
    });
  }

  // Obtener datos de poliza para email y contacto
  getPolicies(){
        let request: PoliciesRequest = {
          LossDate: this.lossDate!,
          PolicyNumber: this.policy,
          Channel: this.channel,
          ChannelCode: this.channelCode
        };

        this.accidentService.getData('Policies', request).subscribe({
          next: (response) => {
            if (response.policies.length) {
              this.emailAddress = response.policies[0].emailAddress;
              this.phoneNumber = response.policies[0].celular;
            }
          },
          error: (error) => {
            console.error('Error al obtener las pólizas:', error);
            if (error.statusCode === 404) {
              console.warn('No se encontraron pólizas para los datos ingresados.');
            }
          },
        });
      }

  // Descargar información de consulta
  downloadClaim() {
    const params = {
      data: this.claimConsultData!,
      usuario: this.authService.getUsername() || '',
      document: this.document!,
      vehicle: this.vehicle!,
      coverage: this.coverage!,
      lossDate: this.lossDateFormatted!,
      emailAddress: this.emailAddress!,
      phoneNumber: this.phoneNumber!
    };
    descargarConsultaAccidente(params);
    this.gAnalytics.trackEvent('descargar_reclamo',{
        action:'Descargar información de un reclamo',
        page:'/accidentes',
      })
  }

  // Modal de datos de consulta de reclamo
  onShowModal() {
    if (this.showModal) {
      this.eventNumber = '';
    }
    this.showModal = !this.showModal;
  }

  // Mostrar notificación
  onShowNotificacion(
    type: 'success' | 'error' | 'warning',
    title: string,
    message: string
  ) {
    this.notificationType = type;
    this.notificationTitle = title;
    this.notificationMessage = message;
    this.showNotification = true;

    setTimeout(() => {
      this.showNotification = false;
    }, 5000);
  }

  onlyNumbers (event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9-]/g, '').trim();
  }

}
